export type SoundName = "shoot" | "pickup" | "steal" | "eliminate" | "shield" | "boost" | "hit" | "streak";

type Tone = {
  /** Start frequency in Hz. */
  from: number;
  /** Frequency at the end of the sweep; equal to `from` for a flat blip. */
  to: number;
  duration: number;
  gain: number;
  wave: OscillatorType;
  /** Floor between two plays of this sound, so bursts cannot machine-gun. */
  minInterval: number;
};

const TONES: Record<SoundName, Tone> = {
  shoot: { from: 660, to: 320, duration: 0.12, gain: 0.05, wave: "triangle", minInterval: 0.06 },
  pickup: { from: 880, to: 1180, duration: 0.07, gain: 0.03, wave: "sine", minInterval: 0.05 },
  steal: { from: 420, to: 900, duration: 0.2, gain: 0.07, wave: "sawtooth", minInterval: 0.1 },
  eliminate: { from: 300, to: 90, duration: 0.42, gain: 0.09, wave: "sawtooth", minInterval: 0.12 },
  shield: { from: 520, to: 1040, duration: 0.26, gain: 0.06, wave: "sine", minInterval: 0.15 },
  boost: { from: 240, to: 620, duration: 0.18, gain: 0.05, wave: "square", minInterval: 0.15 },
  hit: { from: 320, to: 140, duration: 0.16, gain: 0.07, wave: "square", minInterval: 0.08 },
  // The only rising, long, loud tone in the set. Everything else in a fight sweeps
  // downward, so a streak announcing itself cannot be mistaken for anything else.
  streak: { from: 520, to: 1560, duration: 0.55, gain: 0.085, wave: "triangle", minInterval: 0.5 },
};

export type SoundBoard = {
  setMuted(muted: boolean): void;
  /** Must be called from a user gesture; browsers block audio started any other way. */
  unlock(): void;
  play(name: SoundName): void;
  dispose(): void;
};

/**
 * Tiny synthesised SFX layer — no audio assets, no network, and silent until the
 * player interacts. Returns a no-op board when WebAudio is unavailable so callers
 * never have to null-check.
 */
export function createSoundBoard(): SoundBoard {
  const AudioContextCtor = typeof window === "undefined"
    ? undefined
    : window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!AudioContextCtor) {
    return { setMuted() {}, unlock() {}, play() {}, dispose() {} };
  }

  let context: AudioContext | null = null;
  let muted = false;
  const lastPlayed = new Map<SoundName, number>();

  const ensureContext = () => {
    if (!context) context = new AudioContextCtor();
    if (context.state === "suspended") void context.resume();
    return context;
  };

  return {
    setMuted(next) {
      muted = next;
    },
    unlock() {
      if (muted) return;
      try { ensureContext(); } catch { /* audio is optional */ }
    },
    play(name) {
      if (muted || !context) return;
      const tone = TONES[name];
      const now = context.currentTime;
      const previous = lastPlayed.get(name);
      if (previous !== undefined && now - previous < tone.minInterval) return;
      lastPlayed.set(name, now);

      const oscillator = context.createOscillator();
      const amplifier = context.createGain();
      oscillator.type = tone.wave;
      oscillator.frequency.setValueAtTime(tone.from, now);
      oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, tone.to), now + tone.duration);
      amplifier.gain.setValueAtTime(tone.gain, now);
      amplifier.gain.exponentialRampToValueAtTime(0.0001, now + tone.duration);
      oscillator.connect(amplifier).connect(context.destination);
      oscillator.start(now);
      oscillator.stop(now + tone.duration);
    },
    dispose() {
      void context?.close();
      context = null;
    },
  };
}
